/* eslint-disable */
/**
 * SettingsHub — single page that hosts every settings sub-screen with a left
 * sidebar nav. The previously-scattered settings pages (general, order
 * notifications, integrations, team members, webhooks) are all rendered here.
 *
 * Routing model: one URL (`/brand/settings`) for the hub plus a hash (e.g.
 * `#notifications`) to pick the active section. Hash routing avoids touching
 * the React Router config — the parent /admin and /brand layouts stay
 * unchanged. Browser back/forward + bookmarks keep working because hash
 * changes go through the History API.
 *
 * The legacy stand-alone pages (`/team`, `/webhooks`, `/order-notifications`)
 * stay registered as `invisible: true` routes in routes.js so any external
 * deep links still resolve. New navigation should always go through this hub.
 */
import { useEffect, useMemo, useState } from "react";
import { Container } from "reactstrap";
import { Store } from "StoreContext";
import { Link, useLocation } from "react-router-dom";

// Existing section components — rendered as-is. We don't restructure their
// internals so the diff stays small and behavior unchanged.
import { OrderNotificationsForm } from "./OrderNotifications";
import TeamMembers from "./TeamMembers";
import Webhooks from "./Webhooks";
import Integrations from "./Integrations";
import WebsiteEmbed from "./WebsiteEmbed";
import ApiKeys from "./ApiKeys";

// =============================================================================
// Section registry — controls the left-nav order, labels, descriptions, and
// which user roles see each entry. Adding a new section only requires:
//   1. Implement the component
//   2. Append a row here with a unique `id`
//   3. Wire any role visibility via `roles` (omit to show to all)
// =============================================================================
const SECTIONS = [
  {
    id: "notifications",
    label: "Order Notifications",
    description:
      "How you and your customers are notified when a redemption happens.",
    component: () => <OrderNotificationsForm />,
  },
  {
    id: "integrations",
    label: "Integrations",
    description:
      "Connect Klaviyo for scanner events and ShipStation for fulfillment automation. Each card stores its own credentials and can be toggled independently.",
    component: Integrations,
  },
  {
    id: "team",
    label: "Team Members",
    description:
      "Invite teammates to help manage your brand. Each role grants a specific slice of access — owners full, staff fulfillment + edits, rewards manager fulfillment only, readonly view only.",
    component: TeamMembers,
  },
  {
    id: "embed",
    label: "Website Embed",
    description:
      "Copy-paste code that puts the VeriScan verify widget on any website — no plugin, no files to host. Hosted and versioned by VeriScan.",
    component: WebsiteEmbed,
  },
  {
    id: "api-keys",
    label: "API Keys",
    description:
      "Keys for the VeriScan API: verify codes from your own site or app, sync customers and points, fulfill rewards, and pull scan stats and counterfeit alerts.",
    component: ApiKeys,
  },
  {
    id: "webhooks",
    label: "Webhooks",
    description:
      "Subscribe URLs to receive real-time POSTs when scanner events happen — signups, scans, redemptions, and fulfillment updates.",
    component: Webhooks,
  },
];

// Resolve `window.location.hash` (`#notifications`) into a section id, with
// safe fallback to the first section. Defensive against hashes that don't
// match any registered section so a stale bookmark can't render an empty pane.
function readHashSection(hash) {
  const raw = (hash || "").replace(/^#/, "").trim().toLowerCase();
  if (!raw) return SECTIONS[0].id;
  const match = SECTIONS.find((s) => s.id === raw);
  return match ? match.id : SECTIONS[0].id;
}

const SettingsHub = () => {
  const store = Store();
  const { user } = store || {};
  const location = useLocation();

  // Track active section in component state but keep it in sync with the URL
  // hash so deep links + browser back work. Listen for hashchange so a
  // sidebar Link with hash navigation flips the pane without remounting.
  const [activeId, setActiveId] = useState(() =>
    readHashSection(typeof window !== "undefined" ? window.location.hash : "")
  );

  useEffect(() => {
    setActiveId(readHashSection(location.hash));
  }, [location.hash]);

  useEffect(() => {
    const onHashChange = () => setActiveId(readHashSection(window.location.hash));
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const visibleSections = useMemo(() => {
    return SECTIONS.filter((s) => {
      if (!s.roles || s.roles.length === 0) return true;
      return s.roles.includes(user?.role || "brand");
    });
  }, [user?.role]);

  const activeSection =
    visibleSections.find((s) => s.id === activeId) || visibleSections[0];
  const ActiveComponent = activeSection?.component;

  return (
    <Container fluid style={{ fontFamily: "Outfit", paddingTop: 8 }}>
      <h1 className="vs-page-title" style={{ marginBottom: 4 }}>Settings</h1>
      <p style={{ color: "#6B7280", fontSize: 14, marginBottom: 20 }}>
        Manage your brand, integrations, team, and outbound webhooks — all from
        one place.
      </p>

      <div className="vs-settings-layout">
        {/* Left nav */}
        <aside className="vs-settings-nav">
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {visibleSections.map((s) => {
              const isActive = s.id === activeSection?.id;
              return (
                <li key={s.id} style={{ marginBottom: 4 }}>
                  <Link
                    to={{ pathname: location.pathname, hash: `#${s.id}` }}
                    aria-current={isActive ? "page" : undefined}
                    style={{
                      display: "block",
                      padding: "10px 14px",
                      borderRadius: 8,
                      fontSize: 14,
                      fontWeight: isActive ? 600 : 500,
                      color: isActive ? "#362FD8" : "#374151",
                      background: isActive ? "rgba(54, 47, 216, 0.08)" : "transparent",
                      textDecoration: "none",
                      transition: "background 120ms, color 120ms",
                    }}
                  >
                    {s.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* Active section */}
        <section className="vs-settings-content">
          {activeSection && (
            <>
              <header style={{ marginBottom: 16 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: "#1C1C1C", margin: 0 }}>
                  {activeSection.label}
                </h2>
                {activeSection.description && (
                  <p style={{ color: "#6B7280", fontSize: 13, marginTop: 4, marginBottom: 0 }}>
                    {activeSection.description}
                  </p>
                )}
              </header>
              {ActiveComponent ? <ActiveComponent /> : null}
            </>
          )}
        </section>
      </div>

      {/* Inline layout styles. Kept local to avoid touching argon scss. */}
      <style>{`
        .vs-settings-layout {
          display: grid;
          grid-template-columns: 220px 1fr;
          gap: 24px;
          align-items: start;
        }
        .vs-settings-nav {
          position: sticky;
          top: 16px;
          background: #fff;
          border-radius: 12px;
          padding: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
        }
        .vs-settings-content {
          min-width: 0;
        }
        @media (max-width: 768px) {
          .vs-settings-layout {
            grid-template-columns: 1fr;
          }
          .vs-settings-nav {
            position: static;
          }
        }
      `}</style>
    </Container>
  );
};

export default SettingsHub;
